import React, { useState, useEffect } from 'react';
import { Card, Button, Alert, Spinner, Badge, Form, ListGroup } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { useProvisioningWizard } from '../../../hooks/useProvisioningWizard';
import { useProvisioningApi } from '../../../hooks/useProvisioningApi';
import { getCustomersForDropdown } from '../../../services/api/customerApi';
import WizardStepIndicator from './WizardStepIndicator';
import WizardEnvironmentHeader from './WizardEnvironmentHeader';
import CustomerCreationStep from './steps/CustomerCreationStep';
import RulesetConfigStep from './steps/RulesetConfigStep';
import MetadataFieldsStep from './steps/MetadataFieldsStep';
import DeliveryConfigStep from './steps/DeliveryConfigStep';
import PipelineInfoStep from './steps/PipelineInfoStep';
import XmlSchemaStep from './steps/XmlSchemaStep';
import JobCreationStep from './steps/JobCreationStep';
import ValidationStep from './steps/ValidationStep';
import EnableCustomerStep from './steps/EnableCustomerStep';

/**
 * ProvisioningWizard - Main orchestrator for the 9-step provisioning wizard.
 * Manages session lifecycle, step rendering, and navigation.
 */
const ProvisioningWizard = () => {
  const wizard = useProvisioningWizard();
  const api = useProvisioningApi();
  const [searchParams] = useSearchParams();
  const [showResumePrompt, setShowResumePrompt] = useState(false);
  const [showFlowSelection, setShowFlowSelection] = useState(false);
  const [existingSessions, setExistingSessions] = useState([]);

  // Flow selection state
  const [customers, setCustomers] = useState([]);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [loadingCustomers, setLoadingCustomers] = useState(false);

  // Check for existing sessions on mount — offer resume or start new
  useEffect(() => {
    if (!wizard.loading && !wizard.hasActiveSession && !wizard.isProduction && !wizard.error && !wizard.sessionId) {
      const flowType = searchParams.get('flowType');
      const customerId = searchParams.get('customerId') || undefined;

      // If flow type is explicitly specified in URL, skip flow selection
      if (flowType) {
        wizard.checkForExistingSessions().then(sessions => {
          if (sessions.length > 0 && !flowType.includes('existing_customer')) {
            setExistingSessions(sessions);
            setShowResumePrompt(true);
          } else {
            const sessionOptions = { flowType };
            if (customerId) sessionOptions.customerId = customerId;
            wizard.startNewSession(sessionOptions).catch(() => {});
          }
        });
      } else {
        // No flow type in URL — check for resume, then show flow selection
        wizard.checkForExistingSessions().then(sessions => {
          if (sessions.length > 0) {
            setExistingSessions(sessions);
            setShowResumePrompt(true);
          } else {
            setShowFlowSelection(true);
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizard.loading, wizard.isProduction]);

  // Handle resume choice
  const handleResume = (sessionId) => {
    setShowResumePrompt(false);
    wizard.resumeSession(sessionId);
  };

  const handleStartNew = () => {
    setShowResumePrompt(false);
    const flowType = searchParams.get('flowType');
    if (flowType) {
      // URL specifies flow — use it directly
      const customerId = searchParams.get('customerId') || undefined;
      const sessionOptions = { flowType };
      if (customerId) sessionOptions.customerId = customerId;
      wizard.startNewSession(sessionOptions).catch(() => {});
    } else {
      // No URL flow — show flow selection
      setShowFlowSelection(true);
    }
  };

  // Flow selection handlers
  const handleSelectNewCustomer = () => {
    setShowFlowSelection(false);
    wizard.startNewSession({ flowType: 'new_customer' }).catch(() => {});
  };

  const handleSelectExistingCustomer = async () => {
    setLoadingCustomers(true);
    try {
      const response = await getCustomersForDropdown();
      setCustomers(response.data.items || []);
    } catch {
      setCustomers([]);
    } finally {
      setLoadingCustomers(false);
    }
  };

  const handleConfirmExistingCustomer = () => {
    if (!selectedCustomer) return;
    setShowFlowSelection(false);
    wizard.startNewSession({
      flowType: 'existing_customer_new_ruleset',
      customerId: selectedCustomer.id
    }).catch(() => {});
  };

  if (wizard.loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (wizard.error) {
    return (
      <Alert variant="danger" className="mt-3" dismissible onClose={wizard.clearError}>
        <Alert.Heading>Error</Alert.Heading>
        <p>{wizard.error}</p>
        <Button variant="outline-danger" size="sm" onClick={() => wizard.startNewSession()}>
          Retry
        </Button>
      </Alert>
    );
  }

  if (wizard.isProduction) {
    return (
      <Alert variant="warning" className="mt-3">
        <Alert.Heading>Production Environment</Alert.Heading>
        <p>Customer provisioning is not available in the Production environment. Please use the Test environment.</p>
      </Alert>
    );
  }

  // Show resume prompt if an in-progress session exists (only the most recent one)
  if (showResumePrompt && existingSessions.length > 0) {
    const session = existingSessions[0]; // Most recent session (sorted by updatedAt desc from API)
    return (
      <div className="provisioning-wizard">
        <WizardEnvironmentHeader environment={wizard.environment} />
        <Card className="mt-3">
          <Card.Header><h5 className="mb-0">Resume Provisioning Session?</h5></Card.Header>
          <Card.Body>
            <p>You have an unfinished provisioning session. Would you like to resume or start fresh?</p>
            <Alert variant="info">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <strong>{wizard.stepNames[session.currentStep - 1] || `Step ${session.currentStep}`}</strong> — Step {session.currentStep} of 9
                  {session.customerId && <span className="text-muted ms-2">(Customer: {session.customerId.substring(0, 8)}...)</span>}
                  <br />
                  <small className="text-muted">Last updated: {new Date(session.updatedAt).toLocaleString()}</small>
                </div>
              </div>
            </Alert>
            <div className="mt-3 d-flex gap-2">
              <Button variant="primary" onClick={() => handleResume(session.id)}>
                Resume Session
              </Button>
              <Button variant="outline-secondary" onClick={handleStartNew}>
                Start New
              </Button>
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  }

  // Show flow selection — ask user what they want to do
  if (showFlowSelection) {
    const filteredCustomers = customers.filter(c =>
      (c.customerName.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
       c.customerCode.toLowerCase().includes(customerSearchTerm.toLowerCase()))
    );

    return (
      <div className="provisioning-wizard">
        <WizardEnvironmentHeader environment={wizard.environment} />
        <Card className="mt-3">
          <Card.Header><h5 className="mb-0">Customer Provisioning</h5></Card.Header>
          <Card.Body>
            <p>What would you like to do?</p>
            <div className="d-flex gap-3 mb-4">
              <Button variant="primary" size="lg" onClick={handleSelectNewCustomer}>
                Provision New Customer
              </Button>
              <Button
                variant="outline-primary"
                size="lg"
                onClick={handleSelectExistingCustomer}
                disabled={loadingCustomers || customers.length > 0}
              >
                {loadingCustomers ? (
                  <><Spinner animation="border" size="sm" className="me-2" />Loading...</>
                ) : (
                  'Add Ruleset to Existing Customer'
                )}
              </Button>
            </div>

            {customers.length > 0 && (
              <div>
                <Form.Group className="mb-3">
                  <Form.Label htmlFor="customerSearch">Search for a customer</Form.Label>
                  <Form.Control
                    id="customerSearch"
                    type="text"
                    placeholder="Type customer name or code..."
                    value={customerSearchTerm}
                    onChange={(e) => setCustomerSearchTerm(e.target.value)}
                    autoFocus
                  />
                </Form.Group>
                <ListGroup style={{ maxHeight: '250px', overflowY: 'auto' }}>
                  {filteredCustomers.slice(0, 50).map(customer => (
                    <ListGroup.Item
                      key={customer.id}
                      action
                      active={selectedCustomer?.id === customer.id}
                      onClick={() => setSelectedCustomer(customer)}
                    >
                      <strong>{customer.customerName}</strong>
                      <span className="text-muted ms-2">({customer.customerCode})</span>
                    </ListGroup.Item>
                  ))}
                  {filteredCustomers.length === 0 && (
                    <ListGroup.Item className="text-muted text-center">
                      No customers match &quot;{customerSearchTerm}&quot;
                    </ListGroup.Item>
                  )}
                </ListGroup>
                {selectedCustomer && (
                  <div className="mt-3">
                    <Alert variant="success" className="d-flex justify-content-between align-items-center">
                      <span>Selected: <strong>{selectedCustomer.customerName}</strong> ({selectedCustomer.customerCode})</span>
                      <Button variant="success" onClick={handleConfirmExistingCustomer}>
                        Continue with this customer →
                      </Button>
                    </Alert>
                  </div>
                )}
              </div>
            )}
          </Card.Body>
        </Card>
      </div>
    );
  }

  const renderCurrentStep = () => {
    const stepProps = { wizard, api };

    // If current step is skipped, show a badge and auto-advance
    if (wizard.isStepSkipped(wizard.currentStep)) {
      return (
        <Alert variant="info" className="text-center">
          <Badge bg="success" className="me-2">✓</Badge>
          <strong>{wizard.stepNames[wizard.currentStep - 1]}</strong> — Already configured for this customer.
          <div className="mt-2">
            <Button variant="outline-primary" size="sm" onClick={wizard.goToNextStep}>
              Continue to next step →
            </Button>
          </div>
        </Alert>
      );
    }

    switch (wizard.currentStep) {
      case 1: return <CustomerCreationStep {...stepProps} />;
      case 2: return <RulesetConfigStep {...stepProps} />;
      case 3: return <MetadataFieldsStep {...stepProps} />;
      case 4: return <DeliveryConfigStep {...stepProps} />;
      case 5: return <PipelineInfoStep {...stepProps} />;
      case 6: return <XmlSchemaStep {...stepProps} />;
      case 7: return <JobCreationStep {...stepProps} />;
      case 8: return <ValidationStep {...stepProps} />;
      case 9: return <EnableCustomerStep {...stepProps} />;
      default: return null;
    }
  };

  return (
    <div className="provisioning-wizard">
      <WizardEnvironmentHeader environment={wizard.environment} />

      <Card className="mt-3">
        <Card.Header>
          <WizardStepIndicator
            currentStep={wizard.currentStep}
            totalSteps={wizard.totalSteps}
            stepNames={wizard.stepNames}
            stepCompletion={wizard.stepCompletion}
          />
        </Card.Header>
        <Card.Body>
          {renderCurrentStep()}
        </Card.Body>
        <Card.Footer className="d-flex justify-content-between">
          <Button
            variant="outline-secondary"
            onClick={wizard.goToPreviousStep}
            disabled={!wizard.canGoBack}
          >
            ← Previous
          </Button>
          <span className="text-muted align-self-center">
            Step {wizard.currentStep} of {wizard.totalSteps}
          </span>
          <Button
            variant="primary"
            onClick={wizard.goToNextStep}
            disabled={!wizard.canGoForward}
          >
            Next →
          </Button>
        </Card.Footer>
      </Card>
    </div>
  );
};

export default ProvisioningWizard;
